import { Application } from '@platform/client';
import { OrganizationApplicationParameters, UploadResponse } from '@platform/client/schemas';
import { ErrorBox } from '@platform/components';
import { useState } from 'react';
import { FileTrigger } from 'react-aria-components';
import { useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import { ApplicationSuccessIcon } from '../../components/ApplicationSuccessIcon';
import { UploadButton } from '../../components/UploadButton';
import BackgroundImage from '../../images/Leftsection3.svg';

type FormValues = {
  orgLogo: string;
};

export const OrgLogoUpload = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress, dataKey } = { ...props };
  const [file, setFile] = useState<File>();
  const [error, setError] = useState('');

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<OrganizationApplicationParameters>(dataKey);

  const uploadLogo = Application.useUploadLogo();

  const existingLogo = getApplicationDataByKey('orgLogo');

  const { handleSubmit } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      orgLogo: existingLogo || '',
    },
  });

  const submit = () => {
    if (file) {
      uploadLogo.mutate(
        {
          data: {
            file,
          },
        },
        {
          onSuccess: (response) => {
            updateApplicationData({ orgLogo: (response as UploadResponse).url });
            onContinue();
          },
        },
      );
    } else if (!existingLogo) {
      setError("Upload your organization's logo to continue.");
    } else {
      onContinue();
    }
  };

  return (
    <ApplicationBackground
      progress={progress}
      isOrganization={true}
      onBack={onBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
      isEditProfile={props.isEditProfile}
    >
      <ApplicationPageContent
        title="Add your organization's logo"
        description='This helps others quickly recognize who you are. Just a logo - no headshots. You can change it later if needed!'
      >
        <FileTrigger
          acceptedFileTypes={['image/png', 'image/jpg', 'image/jpeg']}
          onSelect={(e) => {
            const files = e ? Array.from(e) : [];
            setFile(files[0]);
            setError('');
          }}
        >
          {!file && !existingLogo && (
            <UploadButton
              text={'Upload organization logo'}
              hintText='.jpeg or .png, At least 200x200px and smaller than 10MB'
              defaultIcon={'building-06'}
              middleIcon='plus-circle'
            />
          )}
          {file && (
            <UploadButton
              text={'Looking good!'}
              hintText='.jpeg or .png, At least 200x200px and smaller than 10MB'
              defaultIcon={'building-06'}
              avatarUrl={URL.createObjectURL(file)}
              middleIcon={<ApplicationSuccessIcon width={20} height={20} />}
            />
          )}
          {!file && existingLogo && (
            <UploadButton
              text={'Looking good!'}
              hintText='.jpeg or .png, At least 200x200px and under 10MB'
              avatarUrl={existingLogo}
              middleIcon={<ApplicationSuccessIcon width={20} height={20} />}
            />
          )}
        </FileTrigger>
        {error && <ErrorBox showAlertTriangle={true}>{error}</ErrorBox>}
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
